import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { CVData } from "@/lib/cv-templates";

// Render an ATS-friendly CV PDF on the server and return base64 for the
// client to trigger a clean file download. Authenticated portal users only.
export const generateCvPdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { cv: CVData; accentHex?: string; filename?: string; unbranded?: boolean }) => {
      if (!input?.cv || typeof input.cv !== "object") {
        throw new Error("Missing CV data");
      }
      const cv = input.cv as CVData;
      // Cap absurd inputs so a single request can't generate a 100-page PDF
      const trim = (s: string | undefined, n: number) =>
        typeof s === "string" ? s.slice(0, n) : s;
      cv.fullName = trim(cv.fullName, 120) || "Candidate";
      cv.headline = trim(cv.headline, 200) || "";
      cv.summary = trim(cv.summary, 2000) || "";
      cv.skills = (cv.skills || []).slice(0, 40).map((s) => String(s).slice(0, 80));
      cv.experience = (cv.experience || []).slice(0, 15).map((e: any) => ({
        role: trim(e.role, 160) || "",
        company: trim(e.company, 160) || "",
        location: trim(e.location, 120) || "",
        start: trim(e.start, 40) || "",
        end: trim(e.end, 40) || "",
        bullets: (e.bullets || []).slice(0, 12).map((b: any) => String(b).slice(0, 400)),
      }));
      cv.education = (cv.education || []).slice(0, 10);
      cv.certifications = (cv.certifications || []).slice(0, 20);
      cv.achievements = (cv.achievements || []).slice(0, 12).map((s: any) => String(s).slice(0, 300));
      cv.awards = (cv.awards || []).slice(0, 12).map((a: any) => ({
        title: typeof a?.title === "string" ? a.title.slice(0, 160) : "",
        issuer: typeof a?.issuer === "string" ? a.issuer.slice(0, 160) : undefined,
        year: typeof a?.year === "string" ? a.year.slice(0, 40) : undefined,
        detail: typeof a?.detail === "string" ? a.detail.slice(0, 300) : undefined,
      }));
      cv.languages = (cv.languages || []).slice(0, 12);
      return {
        cv,
        accentHex: typeof input.accentHex === "string" ? input.accentHex.slice(0, 7) : undefined,
        filename: typeof input.filename === "string" ? input.filename.slice(0, 80) : undefined,
        unbranded: input.unbranded === true,
      };
    },
  )
  .handler(async ({ data }) => {
    const { renderCvPdf } = await import("@/lib/cv-pdf.server");
    const { bytes } = await renderCvPdf(data.cv, data.accentHex, { unbranded: data.unbranded });
    // base64 for safe transport over JSON RPC
    let bin = "";
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const CHUNK = 0x8000;
    for (let i = 0; i < u8.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, Array.from(u8.subarray(i, i + CHUNK)) as any);
    }
    const base64 = btoa(bin);
    const filename =
      data.filename?.replace(/[^a-z0-9_\-. ]/gi, "_") ||
      `${(data.cv.fullName || "CV").replace(/[^a-z0-9_\- ]/gi, "_")}_CV.pdf`;
    return { base64, filename, contentType: "application/pdf" };
  });
